import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { humanAdoptionStatus } from "@/lib/humanAdoptionStatus";

type UnaRow = {
  id: string;
  name: string | null;
  current_edition_eligible: boolean | null;
};

type AdoptionRow = {
  id: string;
  una_id: string;
  status: string;
  review_status: string;
  created_at: string;
  executed_on: string | null;
};

type SignerCount = { signed: number; total: number };

export default function AdminAdoptionDashboard() {
  const [unas, setUnas] = useState<UnaRow[]>([]);
  const [adoptions, setAdoptions] = useState<Record<string, AdoptionRow>>({});
  const [counts, setCounts] = useState<Record<string, SignerCount>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: unaRows }, { data: adoptionRows }] = await Promise.all([
      (supabase as any)
        .from("unas")
        .select("id, name, current_edition_eligible")
        .order("name", { ascending: true })
        .limit(500),
      (supabase as any)
        .from("constitution_adoptions")
        .select("id, una_id, status, review_status, created_at, executed_on")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);

    const byUna: Record<string, AdoptionRow> = {};
    for (const row of (adoptionRows ?? []) as AdoptionRow[]) {
      if (!byUna[row.una_id]) byUna[row.una_id] = row;
    }

    const ids = Object.values(byUna).map((a) => a.id);
    const nextCounts: Record<string, SignerCount> = {};
    if (ids.length > 0) {
      const { data: signerRows } = await (supabase as any)
        .from("constitution_adoption_signers")
        .select("adoption_id, status")
        .in("adoption_id", ids);
      for (const s of (signerRows ?? []) as { adoption_id: string; status: string }[]) {
        const entry = nextCounts[s.adoption_id] ?? { signed: 0, total: 0 };
        entry.total += 1;
        if (s.status === "signed") entry.signed += 1;
        nextCounts[s.adoption_id] = entry;
      }
    }

    setUnas((unaRows ?? []) as UnaRow[]);
    setAdoptions(byUna);
    setCounts(nextCounts);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEligible = async (una: UnaRow, next: boolean) => {
    setBusy(una.id);
    const { data: authData } = await supabase.auth.getUser();
    const { error } = await (supabase as any)
      .from("unas")
      .update({
        current_edition_eligible: next,
        current_edition_eligible_at: next ? new Date().toISOString() : null,
        current_edition_eligible_by: next ? authData?.user?.id ?? null : null,
      })
      .eq("id", una.id);
    setBusy(null);

    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(next ? "Marked eligible" : "Eligibility removed");
    void load();
  };

  const visible = unas.filter((u) =>
    (u.name ?? "").toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Current edition adoption</h1>
          <p className="text-sm text-muted-foreground">
            Eligibility, package review, and signature progress by Association.
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()} className="min-h-11">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Associations</CardTitle>
          <CardDescription>Turn eligibility on to open the flow for a member.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by Association name"
            className="max-w-sm"
          />

          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Association</TableHead>
                    <TableHead>Eligible</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Signatures</TableHead>
                    <TableHead className="text-right">Package</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((una) => {
                    const adoption = adoptions[una.id];
                    const count = adoption ? counts[adoption.id] : undefined;
                    return (
                      <TableRow key={una.id}>
                        <TableCell className="font-medium">{una.name ?? "Unnamed"}</TableCell>
                        <TableCell>
                          <Switch
                            checked={una.current_edition_eligible === true}
                            disabled={busy === una.id}
                            onCheckedChange={(v) => void toggleEligible(una, v)}
                          />
                        </TableCell>
                        <TableCell>
                          {adoption ? (
                            <Badge variant="secondary">{humanAdoptionStatus(adoption.status)}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Not started</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {count ? `${count.signed} of ${count.total}` : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {adoption ? (
                            <Button asChild variant="ghost" size="sm">
                              <Link to={`/admin/adoptions/${adoption.id}`}>Open</Link>
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
